// src/components/features/analysis/SummaryTable.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  SummaryTable as StyledSummaryTable,
  TableHeader,
  TableCell,
  StatusBadge,
  StatusWrapper
} from '../../../styles/AnalysisStyles';
import { apiService } from '../../../services/api';
import { ErrorTooltip } from '../../../styles/HomeStyles';

const SummaryTable = ({ analysisData }) => {
  const [tags, setTags] = useState(analysisData?.tags || []);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [customInput, setCustomInput] = useState('');
  const dropdownRef = useRef(null);

  const [isEditingFamily, setIsEditingFamily] = useState(false);
  const [familyValue, setFamilyValue] = useState(analysisData?.malware_family || 'Unknown');
  const [familyInput, setFamilyInput] = useState('');

  useEffect(() => {
    setFamilyValue(analysisData?.malware_family || 'Unknown');
  }, [analysisData?.malware_family]);

  // Sync tags when analysisData changes (e.g. after SSE update)
  useEffect(() => {
    setTags(analysisData?.tags || []);
  }, [analysisData?.tags]);

  // Load predefined tags once
  useEffect(() => {
    apiService.getPredefinedTags()
      .then(data => setPredefinedTags(data.tags || []))
      .catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isAddingTag) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsAddingTag(false);
        setCustomInput('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isAddingTag]);

  const handleAddTag = async (tag) => {
    const normalized = tag.toLowerCase().trim();
    if (!normalized || tags.includes(normalized)) {
      setIsAddingTag(false);
      setCustomInput('');
      return;
    }
    const prev = tags;
    const optimistic = [...tags, normalized].sort();
    setTags(optimistic);
    setIsAddingTag(false);
    setCustomInput('');
    try {
      const result = await apiService.updateTags(analysisData.sha256, [normalized], []);
      setTags(result.tags);
    } catch {
      setTags(prev);
    }
  };

  const handleRemoveTag = async (tag) => {
    const prev = tags;
    setTags(tags.filter(t => t !== tag));
    try {
      const result = await apiService.updateTags(analysisData.sha256, [], [tag]);
      setTags(result.tags);
    } catch {
      setTags(prev);
    }
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (customInput.trim()) handleAddTag(customInput.trim());
  };

  const handleFamilySave = async () => {
    const trimmed = familyInput.trim();
    if (!trimmed) { setIsEditingFamily(false); return; }
    const prev = familyValue;
    setFamilyValue(trimmed);
    setIsEditingFamily(false);
    try {
      await apiService.updateFamily(analysisData.sha256, trimmed);
    } catch {
      setFamilyValue(prev);
    }
  };

  const availablePredefined = predefinedTags.filter(t => !tags.includes(t));

  return (
    <StyledSummaryTable>
      <tbody>
        <tr>
          <TableHeader>SHA256</TableHeader>
          <TableCell>{analysisData?.sha256}</TableCell>
        </tr>
        <tr>
          <TableHeader>MD5</TableHeader>
          <TableCell>{analysisData?.md5 || 'N/A'}</TableCell>
        </tr>
        <tr>
          <TableHeader>Icon Hash</TableHeader>
            <TableCell>{analysisData?.icon_hash || 'N/A'}</TableCell>
          </tr>
        <tr>
          <TableHeader>App Name</TableHeader>
          <TableCell>{analysisData?.app_name || 'N/A'}</TableCell>
        </tr>
        <tr>
          <TableHeader>Package Name</TableHeader>
          <TableCell>{analysisData?.package_name || 'N/A'}</TableCell>
        </tr>
        <tr>
        <TableHeader>Packed</TableHeader>
          <TableCell>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <StatusBadge
              type={analysisData?.intelligence?.is_packed === 'True' ? 'warning' : 'success'}
                    style={{ marginLeft: '0.5rem' }}
              >
                {analysisData?.intelligence?.is_packed ? 'True' : 'False'}
              </StatusBadge>
              |
              <strong>Type:</strong>
              <StatusBadge
              type={analysisData?.intelligence?.packer_type !== 'N/A' ? 'packed': 'default'}
              >
                    {analysisData?.intelligence?.packer_type ?? 'N/A'}
              </StatusBadge>

            </div>
          </TableCell>
        </tr>

        <tr>
        <TableHeader>Frosting</TableHeader>
        <TableCell>
          <StatusBadge
          type={analysisData?.frosting === true ? 'frosted': 'default'}
          >
            {analysisData?.frosting ? 'True'  : 'False'}
          </StatusBadge>
        </TableCell>
        </tr>

        <tr>
          <TableHeader>Certificate</TableHeader>
          <TableCell>
            {analysisData?.certificates && analysisData.certificates.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {/* Self-issued status with highlighting */}
                <div>
                  <strong>Self Issued:</strong>
                  <StatusBadge
                    type={analysisData.certificates[0].self_issued ? 'warning' : 'success'}
                    style={{ marginLeft: '0.5rem' }}
                  >
                    {analysisData.certificates[0].self_issued ? 'Yes' : 'No'}
                  </StatusBadge>
                </div>

                {/* Certificate hashes */}
                <div>
                  <strong>SHA256:</strong> {analysisData.certificates[0].sha256}<br />
                  <strong>SHA1:</strong> {analysisData.certificates[0].sha1}
                </div>

                {/* Issuer information */}
                <div>
                  <strong>Issuer Details:</strong><br />
                  Common Name: {analysisData.certificates[0].issuer.common_name}<br />
                  Organization: {analysisData.certificates[0].issuer.organization}<br />
                  Organizational Unit: {analysisData.certificates[0].issuer.organizational_unit}<br />
                  Locality: {analysisData.certificates[0].issuer.locality}<br />
                  State/Province: {analysisData.certificates[0].issuer.state_province}<br />
                  Country: {analysisData.certificates[0].issuer.country}
                </div>

                {/* Validity period */}
                <div>
                  <strong>Validity Period:</strong><br />
                  Not Before: {new Date(analysisData.certificates[0].validity.not_before).toLocaleString()}<br />
                  Not After: {new Date(analysisData.certificates[0].validity.not_after).toLocaleString()}
                </div>
              </div>
            ) : (
              'No certificate information available'
            )}
          </TableCell>
        </tr>

        <tr>
          <TableHeader>First Seen</TableHeader>
          <TableCell>
            {analysisData?.first_upload_time
              ? new Date(analysisData.first_upload_time).toLocaleString('en-US', {
                  year: 'numeric', month: 'short', day: 'numeric',
                  hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short'
                })
              : 'Unknown'}
          </TableCell>
        </tr>
        <tr>
          <TableHeader>Last Analyzed</TableHeader>
          <TableCell>
            {analysisData?.timestamp
              ? new Date(analysisData.timestamp).toLocaleString('en-US', {
                  year: 'numeric', month: 'short', day: 'numeric',
                  hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short'
                })
              : 'Unknown'}
          </TableCell>
        </tr>
        <tr>
          <TableHeader>Uploaded By</TableHeader>
          <TableCell>{analysisData?.uploader || 'Unknown'}</TableCell>
        </tr>
        <tr>
          <TableHeader>Malware Family</TableHeader>
          <TableCell>
            {isEditingFamily ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input
                  autoFocus
                  value={familyInput}
                  onChange={e => setFamilyInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleFamilySave(); if (e.key === 'Escape') setIsEditingFamily(false); }}
                  placeholder={familyValue}
                  style={{
                    padding: '0.25rem 0.5rem', border: '1px solid #d1d5db',
                    borderRadius: '4px', fontSize: '0.875rem', width: '160px',
                  }}
                />
                <button
                  onClick={handleFamilySave}
                  style={{
                    padding: '0.25rem 0.6rem', background: '#3b82f6', color: 'white',
                    border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => setIsEditingFamily(false)}
                  style={{
                    padding: '0.25rem 0.6rem', background: 'none', color: '#6b7280',
                    border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <StatusBadge type={familyValue === 'Unknown' ? 'unknown' : 'malware'}>
                  {familyValue}
                </StatusBadge>
                <button
                  onClick={() => { setFamilyInput(familyValue); setIsEditingFamily(true); }}
                  title="Edit malware family"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '0', color: '#9ca3af', fontSize: '0.85rem', lineHeight: 1,
                  }}
                >
                  ✏️
                </button>
              </div>
            )}
          </TableCell>
        </tr>
        <tr>
          <TableHeader>Status</TableHeader>
          <TableCell>
            <StatusWrapper>
              <StatusBadge type={analysisData?.analysis_status || 'unknown'}>
                {analysisData?.analysis_status || 'N/A'}
                {analysisData?.analysis_status === 'failed' && analysisData?.analysis_error && (
                  <ErrorTooltip>ⓘ<span>{analysisData.analysis_error}</span></ErrorTooltip>
                )}
              </StatusBadge>
            </StatusWrapper>
          </TableCell>
        </tr>
        <tr>
          <TableHeader>Tags</TableHeader>
          <TableCell>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem', position: 'relative' }}>
              {tags.map(tag => (
                <span
                  key={tag}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '9999px',
                    fontSize: '0.8rem',
                    fontWeight: '500',
                    background: '#e0e7ff',
                    color: '#3730a3',
                  }}
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '0', color: '#3730a3', fontWeight: '700',
                      lineHeight: 1, fontSize: '0.9rem',
                    }}
                    title={`Remove tag "${tag}"`}
                  >
                    ×
                  </button>
                </span>
              ))}

              {/* + button */}
              <button
                onClick={() => setIsAddingTag(o => !o)}
                style={{
                  background: 'none', border: '1px dashed #6366f1',
                  color: '#6366f1', borderRadius: '9999px',
                  padding: '0.15rem 0.5rem', cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: '600', lineHeight: 1,
                }}
                title="Add tag"
              >
                + Add
              </button>

              {/* Inline dropdown */}
              {isAddingTag && (
                <div
                  ref={dropdownRef}
                  style={{
                    position: 'absolute', top: '100%', left: 0, zIndex: 100,
                    background: 'white', border: '1px solid #e5e7eb',
                    borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    padding: '0.5rem', minWidth: '200px', marginTop: '0.25rem',
                  }}
                >
                  {availablePredefined.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem' }}>
                      {availablePredefined.map(t => (
                        <button
                          key={t}
                          onClick={() => handleAddTag(t)}
                          style={{
                            padding: '0.15rem 0.5rem', borderRadius: '9999px',
                            border: '1px solid #c7d2fe', background: '#f0f4ff',
                            color: '#3730a3', cursor: 'pointer', fontSize: '0.78rem',
                            fontWeight: '500',
                          }}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                  <form onSubmit={handleCustomSubmit} style={{ display: 'flex', gap: '0.3rem' }}>
                    <input
                      autoFocus
                      value={customInput}
                      onChange={e => setCustomInput(e.target.value)}
                      placeholder="Custom tag..."
                      style={{
                        flex: 1, padding: '0.3rem 0.5rem',
                        border: '1px solid #d1d5db', borderRadius: '4px',
                        fontSize: '0.8rem',
                      }}
                    />
                    <button
                      type="submit"
                      style={{
                        padding: '0.3rem 0.6rem', background: '#3b82f6', color: 'white',
                        border: 'none', borderRadius: '4px', cursor: 'pointer',
                        fontSize: '0.8rem',
                      }}
                    >
                      Add
                    </button>
                  </form>
                </div>
              )}
            </div>
          </TableCell>
        </tr>
      </tbody>
    </StyledSummaryTable>
  );
};

export default SummaryTable;
